import React from "react";
import { motion } from "framer-motion";
import { FaHeart, FaGithub, FaLinkedin, FaTwitter, FaGlobe } from "react-icons/fa";
import { BsQuestionLg } from "react-icons/bs";
import NavBar from "./NavBar";
import { useSocialLinks } from "../../hooks/useSocialLinks";

// Mapeo de iconos
const iconMapping = {
    FaLinkedin: FaLinkedin,
    FaGithub: FaGithub,
    FaTwitter: FaTwitter,
    FaGlobe: FaGlobe,
    generic: BsQuestionLg,
};

export default function Footer() {
    const currentYear = new Date().getFullYear();
    const { socialLinks, loading } = useSocialLinks();

    // Filtrar solo los enlaces visibles (excluir "Descargar CV")
    const visibleSocialLinks = socialLinks.filter(link => link.name !== "Descargar CV");

    return (
        <footer className="relative mt-32 py-12 border-t border-gray-200 dark:border-gray-800">
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-100 to-transparent dark:from-gray-900 dark:to-transparent opacity-50 pointer-events-none"></div>

            <div className="relative container mx-auto px-4">
                {/* Social Links */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex justify-center gap-6 mb-8"
                >
                    {loading ? (
                        // Skeleton loading
                        [1, 2].map((i) => (
                            <div key={i} className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse" />
                        ))
                    ) : (
                        visibleSocialLinks.map((social, index) => {
                            const IconComponent = iconMapping[social.icon] || iconMapping.generic;
                            return (
                                <motion.a
                                    key={index}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileHover={{ scale: 1.2, y: -3 }}
                                    whileTap={{ scale: 0.9 }}
                                    className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-500 transition-all duration-300 shadow-lg hover:shadow-xl"
                                    aria-label={social.name}
                                >
                                    <IconComponent className="size-5" />
                                </motion.a>
                            );
                        })
                    )}
                </motion.div>

                {/* Navigation Links - Ahora usando NavBar component */}
                <NavBar 
                    variant="footer"
                    showIcons={false}
                    animateOnMount={true}
                    animationDelay={0.2}
                    className="mb-8"
                />

                {/* Divider */}
                <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="w-48 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent mx-auto mb-8"
                />

                {/* Copyright */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="text-center"
                >
                    <p className="text-gray-600 dark:text-gray-400 text-sm flex items-center justify-center gap-2">
                        <span>&copy; {currentYear}</span>
                        <span>Hecho con</span>
                        <motion.span
                            animate={{
                                scale: [1, 1.2, 1],
                            }}
                            transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <FaHeart className="text-red-500 inline" />
                        </motion.span>
                        <span>por</span>
                        <span className="font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">colidom</span>
                    </p>
                    <p className="text-gray-500 dark:text-gray-500 text-xs mt-2">Algunos derechos reservados, otros en desarrollo</p>
                </motion.div>

                {/* Back to top indicator */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="mt-8 flex justify-center"
                >
                    <motion.button
                        onClick={(e) => {
                            e.preventDefault();
                            document.querySelector('#inicio')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        whileHover={{ y: -3 }}
                        className="text-xs text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200 flex items-center gap-2"
                        type="button"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        Volver arriba
                    </motion.button>
                </motion.div>
            </div>
        </footer>
    );
}
